import React from 'react';
import { fmtDateTime } from '@shared/formatDate';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Chip,
  Link,
} from '@mui/material';
import { Transaction } from '../../types';
import { CanonicalTransactionDetailsDialog } from '@shared/CanonicalTransactionDetailsDialog';
import type { TransactionDetailDto } from '@shared/transactionDetailTypes';
import apiService from '../../services/api';

interface TransactionDetailsDialogProps {
  open: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  onViewMessage?: (mailId: string) => void;
}

/**
 * Fetches canonical detail for a transactionId from the MailBox API.
 * The returned object matches TransactionDetailDto exactly.
 */
async function fetchMailboxTransactionDetail(transactionId: string): Promise<TransactionDetailDto> {
  return apiService.get<TransactionDetailDto>(
    `/api/account/transactions/detail/${encodeURIComponent(transactionId)}`
  );
}

const TransactionDetailsDialog: React.FC<TransactionDetailsDialogProps> = ({ open, onClose, transaction, onViewMessage }) => {
  // Delegate to the canonical shared dialog for any transaction with an ID
  if (transaction?.transactionId) {
    return (
      <CanonicalTransactionDetailsDialog
        open={open}
        onClose={onClose}
        transactionId={transaction.transactionId}
        fetchDetail={fetchMailboxTransactionDetail}
        onViewMessage={onViewMessage}
        title="Transaction Details"
      />
    );
  }

  if (!transaction) return null;

  const DetailRow = ({ label, value }: { label: string; value: React.ReactNode }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {label}:
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right', maxWidth: '60%', wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  );

  // Get values with fallback for legacy properties
  const dateInitiated = transaction.dateInitiated || transaction.date || '';
  const requiredPostage = transaction.requiredPostage ?? transaction.postage ?? 0;
  const postageSystemFee = transaction.postageSystemFee ?? 0;
  const attachedFunds = transaction.attachedFunds ?? transaction.attachedAmount ?? 0;
  const attachedFundsFee = transaction.attachedFundsFee ?? 0;
  const totalSystemFees = postageSystemFee + attachedFundsFee;
  const commercialCategory = transaction.commercialCategory || 'Personal message';
  const rawType = transaction.rawType || transaction.rawTransactionType || '';
  const isProductSplitType = ['PURCHASE', 'CREDIT_PRODUCT_SALE', 'CREDIT_AFFILIATE_COMMISSION', 'CREDIT_FORWARDER_COMMISSION'].includes(rawType);
  const grossSaleAmount = transaction.grossSaleAmount;
  const vendorNetAmount = transaction.vendorNetAmount;
  const affiliatePayoutAmount = transaction.affiliatePayoutAmount;
  const forwarderPayoutAmount = transaction.forwarderPayoutAmount;
  const vendorFeeAmount = transaction.vendorFeeAmount;
  const affiliateFeeAmount = transaction.affiliateFeeAmount;
  const forwarderFeeAmount = transaction.forwarderFeeAmount;
  const hasSplitBreakdown =
    grossSaleAmount != null ||
    vendorNetAmount != null ||
    affiliatePayoutAmount != null ||
    forwarderPayoutAmount != null ||
    vendorFeeAmount != null ||
    affiliateFeeAmount != null ||
    forwarderFeeAmount != null;
  // Use Actual Account Balance (ignore Available Balance and Reserved Balance)
  const balanceBefore = transaction.balanceBefore ?? 0;
  const balanceAfter = transaction.actualBalanceAfter ?? transaction.balanceAfter ?? 0;
  const amount = transaction.amount ?? 0;
  const statusLabel = transaction.type === 'In Inbox'
    ? 'Reserved Funds'
    : (transaction.action || 'Unknown');
  
  // Check if this is a SENDER_PAID_FEES transaction
  const isSenderPaidFees = rawType === 'SENDER_PAID_FEES';

  // Format a rate (0–1) as a tidy percentage string: 0.30 → "30%", 0.027 → "2.7%"
  const formatPct = (v: number) => `${parseFloat((v * 100).toFixed(4))}%`;

  const postageFeeLabel = (() => {
    const pct = transaction.msTransactionPercFee;
    if (!pct) return 'Postage System Fee';
    return `Postage System Fee (${formatPct(pct)})`;
  })();

  const attachedFeeLabel = (() => {
    const below = transaction.chTransactionPercFee;
    const threshold = transaction.chTransactionFeeThreshold;
    const above = transaction.chTransactionPercFeeOverThreshold;
    if (!below) return 'Attached Credits Fee';
    if (!threshold || threshold === 0 || !above || above === 0 || below === above)
      return `Attached Credits Fee (${formatPct(below)})`;
    return `Attached Credits Fee (${formatPct(below)} ≤ $${threshold.toFixed(2)}; ${formatPct(above)} above)`;
  })();
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Transaction Details</DialogTitle>
      <DialogContent dividers>
        <Box>
          <DetailRow label="Transaction ID" value={
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
              {transaction.transactionId}
            </Typography>
          } />
          {transaction.mailId && (
            <DetailRow label="Message ID" value={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', wordBreak: 'break-all' }}>
                  {transaction.mailId}
                </Typography>
                {onViewMessage && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => { onViewMessage(transaction.mailId!); onClose(); }}
                    sx={{ whiteSpace: 'nowrap', fontSize: '0.75rem' }}
                  >
                    View Message →
                  </Link>
                )}
              </Box>
            } />
          )}
          <DetailRow label="Date & Time Sent" value={fmtDateTime(dateInitiated)} />
          {(transaction.type === 'Message' || transaction.type === 'Pending Delivery' ||
            transaction.action === 'Sent / Available' || transaction.action === 'Read' ||
            transaction.action === 'Received') ? (
            <DetailRow
              label="Date & Time Read"
              value={
                transaction.dateCompleted
                  ? fmtDateTime(transaction.dateCompleted)
                  : <Typography variant="body2" color="text.secondary" fontStyle="italic">Not yet read</Typography>
              }
            />
          ) : transaction.dateCompleted ? (
            <DetailRow label="Date & Time Read" value={fmtDateTime(transaction.dateCompleted)} />
          ) : null}
          <DetailRow label="Type" value={transaction.type} />

          {/* For product purchases, sales, and promo fee transactions, show purchase/sale price */}
          {(rawType === 'PURCHASE' || rawType === 'CREDIT_PRODUCT_SALE' || rawType === 'PROMO_FEE' || rawType === 'PROMO_CREDIT_FEE' || transaction.purchasePrice) && (
            <DetailRow
              label={rawType === 'CREDIT_PRODUCT_SALE' ? "Sale Price" : "Purchase Price"}
              value={
                <span style={{ color: rawType === 'CREDIT_PRODUCT_SALE' ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                  ${(transaction.purchasePrice ?? transaction.attachedFunds ?? transaction.attachedAmount ?? Math.abs(amount)).toFixed(2)}
                </span>
              }
            />
          )}

          <DetailRow label="Status" value={
            <Chip
              label={statusLabel}
              size="small"
              color={
                statusLabel === 'Read' ? 'success' :
                statusLabel === 'Sent / Available' ? 'primary' :
                statusLabel === 'Expired' ? 'error' :
                statusLabel === 'Canceled' ? 'default' :
                statusLabel === 'Rejected' ? 'warning' :
                statusLabel === 'Reserved Funds' || statusLabel === 'Reserved' ? 'secondary' :
                'default'
              }
              variant="outlined"
              sx={{ fontSize: '0.75rem', fontWeight: 600 }}
            />
          } />

          {/* For SENDER_PAID_FEES, show fee details differently */}
          {isSenderPaidFees ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Postage Details</Typography>
              <DetailRow label="Sender Paid Postage" value={`$${requiredPostage.toFixed(4)}`} />
              <DetailRow label={postageFeeLabel} value={postageSystemFee > 0 ? `-$${postageSystemFee.toFixed(4)}` : '-'} />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Attached Credits Details</Typography>
              <DetailRow label="Attached Credits" value={
                <span style={{ color: amount >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                  {amount >= 0 ? '+' : '-'}${attachedFunds.toFixed(4)}
                </span>
              } />
              <DetailRow label={attachedFeeLabel} value={attachedFundsFee > 0 ? `-$${attachedFundsFee.toFixed(4)}` : '-'} />
              
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Summary</Typography>
              <DetailRow
                label="Net Amount"
                value={
                  <span style={{
                    color: amount >= 0 ? '#2e7d32' : '#d32f2f',
                    fontWeight: 'bold'
                  }}>
                    {amount >= 0 ? '+' : ''}{`$${amount.toFixed(4)}`}
                  </span>
                }
              />
              <DetailRow label="Total System Fees" value={totalSystemFees > 0 ? `-$${totalSystemFees.toFixed(4)}` : '-'} />
            </>
          ) : (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Postage Details</Typography>
              <DetailRow label="Required Postage" value={requiredPostage > 0 ? `$${requiredPostage.toFixed(4)}` : '-'} />
              <DetailRow label={postageFeeLabel} value={postageSystemFee > 0 ? `-$${postageSystemFee.toFixed(4)}` : '-'} />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Attached Credits Details</Typography>
              <DetailRow label="Attached Credits" value={
                <span style={{ color: amount >= 0 ? '#2e7d32' : '#d32f2f', fontWeight: 'bold' }}>
                  {amount >= 0 ? '+' : '-'}${attachedFunds.toFixed(4)}
                </span>
              } />
              <DetailRow label={attachedFeeLabel} value={attachedFundsFee > 0 ? `-$${attachedFundsFee.toFixed(4)}` : '-'} />

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Summary</Typography>
              <DetailRow
                label="Net Amount"
                value={
                  <span style={{
                    color: amount >= 0 ? '#2e7d32' : '#d32f2f',
                    fontWeight: 'bold'
                  }}>
                    {amount >= 0 ? '+' : ''}{`$${amount.toFixed(4)}`}
                  </span>
                }
              />
              <DetailRow label="Total System Fees" value={totalSystemFees > 0 ? `-$${totalSystemFees.toFixed(4)}` : '-'} />
            </>
          )}

          {/* Split Payment Information - for purchases using both vendor and 2Way credits */}
          {(transaction.promoAmountUsed !== undefined && transaction.promoAmountUsed > 0) || 
           (transaction.regularAmountUsed !== undefined && transaction.regularAmountUsed > 0) ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#7b1fa2' }}>Payment Breakdown</Typography>
              {transaction.promoAmountUsed !== undefined && transaction.promoAmountUsed > 0 && (
                <DetailRow 
                  label="From Vendor Credits" 
                  value={
                    <span style={{ color: '#7b1fa2', fontWeight: 'bold' }}>
                      -${transaction.promoAmountUsed.toFixed(2)}
                    </span>
                  } 
                />
              )}
              {transaction.regularAmountUsed !== undefined && transaction.regularAmountUsed > 0 && (
                <DetailRow 
                  label="From 2Way Credits" 
                  value={
                    <span style={{ color: '#d32f2f', fontWeight: 'bold' }}>
                      -${transaction.regularAmountUsed.toFixed(2)}
                    </span>
                  } 
                />
              )}
            </>
          ) : null}

          {isProductSplitType && hasSplitBreakdown ? (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: '#1565c0' }}>Sale Split Breakdown</Typography>
              <DetailRow
                label="Gross Collected"
                value={grossSaleAmount != null ? `$${grossSaleAmount.toFixed(2)}` : '-'}
              />
              <DetailRow
                label="Vendor Net"
                value={vendorNetAmount != null ? `$${vendorNetAmount.toFixed(2)}` : '-'}
              />
              <DetailRow
                label="Affiliate Payout"
                value={
                  affiliatePayoutAmount != null
                    ? `${transaction.affiliateUserId ? `${transaction.affiliateUserId}: ` : ''}$${affiliatePayoutAmount.toFixed(2)}`
                    : '-'
                }
              />
              <DetailRow
                label="Referrer Payout"
                value={
                  forwarderPayoutAmount != null
                    ? `${transaction.forwarderUserId ? `${transaction.forwarderUserId}: ` : ''}$${forwarderPayoutAmount.toFixed(2)}`
                    : '-'
                }
              />
              <DetailRow
                label="Vendor Fees"
                value={vendorFeeAmount != null ? `$${vendorFeeAmount.toFixed(2)}` : '-'}
              />
              <DetailRow
                label="Affiliate Fees"
                value={affiliateFeeAmount != null ? `$${affiliateFeeAmount.toFixed(2)}` : '-'}
              />
              <DetailRow
                label="Referrer Fees"
                value={forwarderFeeAmount != null ? `$${forwarderFeeAmount.toFixed(2)}` : '-'}
              />
            </>
          ) : null}

          {/* Linked Transaction - for split purchases that have both promo and regular parts */}
          {transaction.linkedTransactionId && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Related Transaction</Typography>
              <DetailRow 
                label="Linked Purchase ID" 
                value={
                  <Typography 
                    variant="body2" 
                    sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                  >
                    {transaction.linkedTransactionId}
                  </Typography>
                } 
              />
            </>
          )}

          <DetailRow
            label="Account Balance Before"
            value={`$${balanceBefore.toFixed(4)}`}
          />
          <DetailRow
            label="Account Balance After"
            value={`$${balanceAfter.toFixed(4)}`}
          />

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Parties</Typography>
          
          {/* Product purchases: Different party views based on transaction type */}
          {(rawType === 'PURCHASE' || rawType === 'CREDIT_PRODUCT_SALE' || rawType === 'CREDIT_AFFILIATE_COMMISSION' || rawType === 'CREDIT_FORWARDER_COMMISSION') ? (
            <>
              <DetailRow label="Product" value={transaction.subject || '-'} />
              
              {/* BUYER VIEW: Only show vendor */}
              {rawType === 'PURCHASE' && (
                <>
                  <DetailRow label="Vendor" value={transaction.toUser || '-'} />
                </>
              )}
              
              {/* VENDOR VIEW: Show buyer, affiliate (if exists), referrer (if exists) */}
              {rawType === 'CREDIT_PRODUCT_SALE' && (
                <>
                  <DetailRow label="Vendor" value={transaction.toUser || '-'} />
                  <DetailRow label="Buyer" value={transaction.fromUser || '-'} />
                  {/* TODO: Show affiliate and referrer info when available from backend */}
                </>
              )}
              
              {/* COMMISSION/ROYALTY RECIPIENT VIEW: Show vendor, their role and buyer */}
              {rawType === 'CREDIT_AFFILIATE_COMMISSION' && (
                <>
                  {transaction.vendorId && (
                    <DetailRow label="Vendor" value={transaction.vendorId} />
                  )}
                  <DetailRow label="Commission/Royalty Recipient" value={transaction.toUser || '-'} />
                  <DetailRow label="Buyer" value={transaction.fromUser || '-'} />
                </>
              )}
              
              {/* REFERRER VIEW: Show vendor, their role and buyer */}
              {rawType === 'CREDIT_FORWARDER_COMMISSION' && (
                <>
                  {transaction.vendorId && (
                    <DetailRow label="Vendor" value={transaction.vendorId} />
                  )}
                  <DetailRow label="Referrer" value={transaction.toUser || '-'} />
                  <DetailRow label="Buyer" value={transaction.fromUser || '-'} />
                </>
              )}
            </>
          ) : (
            <>
              {/* Regular message transactions */}
              {transaction.toUser && (
                <DetailRow label="To (Recipient)" value={transaction.toUser} />
              )}
              {transaction.fromUser && (
                <DetailRow label="From (Payer/Sender)" value={transaction.fromUser} />
              )}
            </>
          )}

          {/* Only show commercial category if not a product purchase */}
          {!['PURCHASE', 'CREDIT_PRODUCT_SALE', 'CREDIT_AFFILIATE_COMMISSION', 'CREDIT_FORWARDER_COMMISSION'].includes(rawType) && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Commercial</Typography>
              <DetailRow label="Commercial Code" value={commercialCategory} />
              {!isSenderPaidFees && transaction.subject && (
                <DetailRow label="Subject" value={transaction.subject} />
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Internal</Typography>
          <DetailRow 
            label="Raw Type" 
            value={
              <Chip 
                label={rawType} 
                size="small" 
                sx={{ 
                  fontFamily: 'monospace',
                  fontSize: '0.7rem',
                  height: '20px'
                }} 
              />
            } 
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransactionDetailsDialog;
